<template>
  <div class="all-courses-page">
    <Navbar />
    <PageBanner :title="pageTitle" />

    <!-- Breadcrumb Area -->
    <Breadcrumb :items="breadcrumbItems" class="intro-breadcrumb" />

    <div class="content">
      <!-- 部長介紹內容 -->
      <div class="plan-origin-content" @click="handleBodyClick">
        <!-- 部長資訊區塊 -->
        <div class="key-person-container">
          <div class="key-person-header">
            <div class="key-person-photo">
              <img src="/src/assets/minister.jpg" alt="Minister Photo" />
            </div>
            <div class="key-person-info">
              <div class="key-person-title">
                {{ $t("ministerIntro.keyPerson.title") }}
              </div>
              <div class="key-person-name">
                {{ $t("ministerIntro.keyPerson.name") }}
              </div>
              <div class="key-person-eng-name">
                {{ $t("ministerIntro.keyPerson.engName") }}
              </div>
            </div>
          </div>

          <!-- 將第一段內容放置在照片下方 -->
          <div class="first-section-container" v-if="false">
            <p class="first-section-text"></p>
          </div>
        </div>

        <!-- 簡介內容 -->
        <div class="section-container">
          <div class="section-text" v-html="introContent"></div>
        </div>
        <!-- Lightbox -->
        <ImageLightbox
          :visible="showLightbox"
          :src="currentLightboxImage"
          :alt="pageTitle"
          @close="closeLightbox"
        />
      </div>
    </div>
    <FooterSection />
  </div>
</template>
  
<script setup >
import { useLayoutStore } from "@/stores/layout";
import { useI18n } from "vue-i18n";
import { computed, onMounted, ref } from "vue";
import Navbar from "@/components/Navbar.vue";
import PageBanner from "@/components/PageBanner.vue";
import FooterSection from "@/components/FooterSection.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import { contentAPI } from "@/services/api";

// 定義響應式變數
const { locale } = useI18n();
const layoutStore = useLayoutStore();

const intro = ref(null);

const isEnglish = computed(() =>
  String(locale.value).toLowerCase().includes("en")
);

const pageTitle = computed(() => {
  if (intro.value) {
    return isEnglish.value
      ? intro.value.title_en || intro.value.title
      : intro.value.title;
  }
  return String(locale.value).toLowerCase().includes("en")
    ? "Message from Minister"
    : "部長的話";
});

const parseImageCaptions = (html) => {
  if (!html) return "";
  return html.replace(/<img([^>]+)>/gi, (imgTag, attrs) => {
    const altMatch = attrs.match(/alt=["']([^"']*)["']/i);
    const altText = altMatch ? altMatch[1].trim() : "";
    if (altText) {
      return `<figure class="rich-figure"><img${attrs}><figcaption class="image-caption">${altText}</figcaption></figure>`;
    }
    return imgTag;
  });
};

const introContent = computed(() => {
  if (!intro.value) return "";
  const val = isEnglish.value
    ? intro.value.content_en || intro.value.content
    : intro.value.content;
  return parseImageCaptions(val);
});

const breadcrumbItems = [
  { label: "首頁", path: "/" },
  { label: "簡介與沿革" },
  { label: "部長的話" },
];

import { useLightbox } from "@/composables/useLightbox";
import ImageLightbox from "@/components/ImageLightbox.vue";

const { showLightbox, currentLightboxImage, closeLightbox, handleBodyClick } =
  useLightbox();

onMounted(async () => {
  try {
    const res = await contentAPI.platformIntroSection("minister_message");
    if (res.data) {
      intro.value = res.data;
    }
  } catch (e) {}
});
</script>
  
  
<style scoped lang="scss">
@use "@/styles/rich-content.scss" as rc;

.all-courses-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: 40px 20px;
  max-width: 1200px;
  width: 100%;
  margin: 80px auto;
}

/* 計畫緣起內容樣式 */
.plan-origin-content {
  background-color: #fff;
  border-radius: 8px;
  line-height: 1.8;
  display: flex;
  flex-direction: column;
}

/* 技監兼處長資訊區塊 */
.key-person-section {
  display: flex;
  gap: 30px;
  align-items: flex-start;
  background-color: #e9f7fe;
  border-radius: 8px;
  padding: 80px;
}

.key-person-photo {
  width: 180px;
  height: 230px;
  overflow: hidden;
  border-radius: 8px;
  margin-top: -160px;
}

.key-person-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.key-person-info {
  flex: 1;
  margin-top: -60px;
}

.key-person-title {
  font-size: 20px;
  font-weight: 600;
  color: #000;
}

.key-person-name {
  font-size: 18px;
  color: #000;
}

.key-person-eng-name {
  font-size: 16px;
  color: #000;
}

.section-container {
  padding: 40px;
  background-color: #fff;
  border-radius: 8px;
  font-size: 20px;

  :deep(p) {
    font-size: 20px;
    color: #333;
    line-height: 1.8;
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul),
  :deep(ol) {
    margin-bottom: 24px;
    padding-left: 24px;
  }

  :deep(li) {
    font-size: 20px;
    color: #333;
    line-height: 1.8;
    margin-bottom: 8px;
  }

  :deep(strong) {
    font-weight: 600;
    color: #022964;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    color: #022964;
    font-weight: 600;
    margin-top: 32px;
    margin-bottom: 16px;
    line-height: 1.4;
  }

  :deep(h1) {
    font-size: 28px;
  }

  /* H2：加底部裝飾線以與 H3 有明顯區別 */
  :deep(h2) {
    font-size: 24px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(2, 41, 100, 0.2);
  }

  /* H3：左側色條，字體稍小 */
  :deep(h3) {
    font-size: 20px;
    padding-left: 12px;
    border-left: 4px solid #022964;
    margin-left: 0;
  }

  :deep(h4) {
    font-size: 18px;
  }

  /* figcaption / 圖片說明文字 */
  :deep(figcaption),
  :deep(.image-caption) {
    font-size: 0.875em;
    color: #666;
    text-align: center;
    margin-top: 0.4em;
    font-style: italic;
  }

  /* 對齊支援 */
  :deep([style*="text-align: center"]) { text-align: center; }
  :deep([style*="text-align: right"]) { text-align: right; }
  :deep([style*="text-align: left"]) { text-align: left; }
}

:deep(.section-text) {
  @include rc.rich-content;
  flex: 1;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .banner-container {
    height: 120px;
    padding: 0 16px;
  }

  .banner-content {
    align-items: flex-start;
    text-align: left;
  }

  .banner-title {
    font-size: 28px;
    text-align: left;
  }

  .content {
    padding: 24px 16px;
  }

  .key-person-section {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .key-person-photo {
    margin-bottom: 16px;
  }

  .section-container {
    padding: 24px 16px;
  }
}

.key-person-container {
  background-color: #e9f7fe;
  border-radius: 8px;
  padding: 80px 80px 40px;
}

.key-person-header {
  display: flex;
  gap: 30px;
}

.key-person-photo {
  width: 180px;
  height: 230px;
  overflow: hidden;
  border-radius: 8px;
  margin-top: -160px;
  flex-shrink: 0;
}

.key-person-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.key-person-info {
  margin-top: -60px;
}

.key-person-title {
  font-size: 20px;
  font-weight: 600;
  color: #000;
}

.key-person-name {
  font-size: 18px;
  color: #000;
}

.key-person-eng-name {
  font-size: 16px;
  color: #000;
}

/* 第一段文字樣式 */
.first-section-container {
  width: 100%;
  padding-left: 0; /* 確保與照片左側對齊 */
}

.first-section-text {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin: 0;
}

/* 響應式調整 */
@media (max-width: 768px) {
  .key-person-container {
    padding: 40px 20px 30px;
  }

  .key-person-header {
    flex-direction: column;
    align-items: center;
  }

  .key-person-photo {
    margin-top: -120px;
    margin-bottom: 16px;
  }

  .key-person-info {
    text-align: center;
    margin-top: 0;
  }

  .first-section-container {
    padding-left: 0;
  }

  .first-section-text {
    text-align: left;
  }
}
.technology-section-img {
  height: 350px;
  margin-bottom: 40px;
}

.technology-section-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:deep(.plan-origin-content img) {
  cursor: zoom-in;
}
</style>
